import axios from 'axios';
import CryptoJS from 'crypto-js';
import { raiseAPIError } from './exceptions';

/**
 * API calls returns an objects formatted with pagination
 * {data: [{}, ...], next: 'url', previous: 'url'}
 *
 * @param {object} data response from ApiCall
 * @param {object} data.data the actual data
 * @returns {Array} Array of objects
 */
const handleListData = ({ data }) => {
  if (data.object === 'list') {
    const arrayData = data.data;
    arrayData.object = data.object;
    arrayData.count = data.count;
    arrayData.next = data.next;
    arrayData.previous = data.previous;
    return Promise.resolve(arrayData);
  }
  return Promise.resolve({ data });
};

export default class RestClient {
  #baseUrl = '';

  #authToken = '';

  #onLogout = null;

  #signingSecret = 'fallback-secret';

  /**
   * Cache the request params before making the call.
   * Used to build the storageKey
   */
  requestParams = null;

  constructor(baseUrl, token, onLogout) {
    this.#baseUrl = baseUrl;
    this.#authToken = token;
    this.#onLogout = onLogout;
  }

  /**
   * Config for REST method call
   *
   * @param {object} axiosConfig Config for the request
   * @returns {Promise} Result of the request
   */
  async #request(axiosConfig) {
    const updatedAxiosConfig = { ...axiosConfig };
    updatedAxiosConfig.url = `${this.#baseUrl}${axiosConfig.url}`;

    // --- INTEGRITY SIGNING START ---
    // The backend API enforces request payload integrity verification (X-Internal-Sig)
    // for API requests. The expected signature payload format is:
    // `${method}|/api/v1/${path}|${timestamp}|${bodyString}`.
    //
    // Use Case:
    // 1. Data Tampering Protection: Guarantees that request parameters and payload body
    //    cannot be modified in transit.
    // 2. Replay Attack Prevention: Verified server-side along with X-Internal-Time timestamp.
    // 3. Payload Body Matching: Serializing bodyString ensures client and server compute
    //    an identical HMAC digest for POST/PUT/PATCH payloads (omitting it causes HTTP 403 errors).
    const timestamp = Date.now().toString();
    const method = (updatedAxiosConfig.method || 'GET').toUpperCase();
    const path = axiosConfig.url; // We sign the relative path

    // Create the message: Method + Path + Timestamp + Body
    const bodyString = updatedAxiosConfig.data
      ? typeof updatedAxiosConfig.data === 'string'
        ? updatedAxiosConfig.data
        : JSON.stringify(updatedAxiosConfig.data)
      : '';
    const message = `${method}|/api/v1/${path}|${timestamp}|${bodyString}`;
    const signature = CryptoJS.HmacSHA256(message, this.#signingSecret).toString();

    // Add integrity headers
    updatedAxiosConfig.headers = {
      ...updatedAxiosConfig.headers,
      'X-Internal-Sig': signature,
      'X-Internal-Time': timestamp,
    };
    // --- INTEGRITY SIGNING END ---

    if (this.#authToken) {
      updatedAxiosConfig.auth = { username: this.#authToken, password: '' };
    }

    try {
      return await axios.request(updatedAxiosConfig);
    } catch (err) {
      if (err.response?.status === 401 && this.#authToken) {
        this.#onLogout();
      } else if (err.response?.data?.detail) {
        return raiseAPIError(err.response);
      }
      return Promise.reject(err);
    }
  }

  /** REST API methods */

  /**
   * Rest: Get
   *
   * @param {string} url Request URL or path
   * @param {object} config Additional axios config
   * @returns {Promise} Response of the request
   */
  async get(url, config) {
    const conf = { ...(config || {}), ...{ url, method: 'get' } };
    return this.#request(conf);
  }

  /**
   * Rest: Delete
   *
   * @param {string} url Request URL or path
   * @param {object} config Additional axios config
   * @returns {Promise} Response of the request
   */
  async delete(url, config) {
    const conf = { ...(config || {}), ...{ url, method: 'delete' } };
    return this.#request(conf);
  }

  /**
   * Rest: Post
   *
   * @param {string} url Request URL or path
   * @param {object} data data to POST
   * @param {object} config Additional axios config
   * @returns {Promise} Response of the request
   */
  async post(url, data, config) {
    const conf = { ...(config || {}), ...{ url, data, method: 'post' } };
    return this.#request(conf);
  }

  /**
   * Rest: Put
   *
   * @param {string} url Request URL or path
   * @param {object} data data to PUT
   * @param {object} config Additional axios config
   * @returns {Promise} Response of the request
   */
  async put(url, data, config) {
    const conf = { ...(config || {}), ...{ url, data, method: 'put' } };
    return this.#request(conf);
  }

  /**
   * Rest: Head
   *
   * @param {string} url Request URL or path
   * @param {object} config Additional axios config
   * @returns {Promise} Response of the request
   */
  async head(url, config) {
    const conf = { ...(config || {}), ...{ url, method: 'head' } };
    return this.#request(conf);
  }

  /**
   * Rest: Patch
   *
   * @param {string} url Request URL or path
   * @param {object} data data to PATCH
   * @param {object} config Additional axios config
   * @returns {Promise} Response of the request
   */
  async patch(url, data, config) {
    const conf = { ...(config || {}), ...{ url, data, method: 'patch' } };
    return this.#request(conf);
  }

  /** helpers */

  /**
   * Delete a resource
   *
   * @param {string} objectURI API object relative URI or path
   * @param {string} id resource id
   * @returns {Promise} Response of the request
   */
  async remove(objectURI, id = 'current') {
    const url = `${objectURI}/${id}/`;
    return this.delete(url);
  }

  /**
   * Call to create a resource
   *
   * @param {string} objectURI API object relative URI or path
   * @param {object} data object to create
   * @returns {Promise} Response of the request
   */
  async create(objectURI, data) {
    const url = `${objectURI}`;
    return this.post(url, data);
  }

  /**
   * Call to update a resource
   *
   * @param {string} objectURI API object relative URI or path
   * @param {object} data object to update
   * @param {string} id resource id
   * @returns {Promise} Response of the request
   */
  async update(objectURI, data, id = 'current') {
    const url = `${objectURI}/${id}/`;
    return this.put(url, data);
  }

  /**
   * Call to update an existing file
   *
   * @param {string} objectURI API object relative URI or path
   * @param {string} field object key
   * @param {Array} files an array of Files
   * @param {string} id resource id
   * @param {boolean} useRessourceId whether or not to use path to ressource id
   * @returns {Promise} Response of the request
   */
  async updateFile(objectURI, field, files, id = 'current', useRessourceId = true) {
    const url = useRessourceId ? `${objectURI}/${id}/file` : objectURI;
    const formData = new FormData();
    Object.values(files).forEach((file) => {
      if (file instanceof File) {
        formData.append(field, file);
      }
    });
    return this.post(url, formData);
  }

  /**
   * Prepare a call to fetch a resource
   *
   * @param {string} objectURI API object relative URI or path
   * @param {string} id resource id
   * @param {object} params Query parameters
   * @returns {Promise} a promise
   */
  retrieve(objectURI, id = 'current', params = {}) {
    if (!id) return Promise.resolve({ data: null });
    const path = `${objectURI}/${id}`;
    this.requestParams = { method: 'GET', params, path };
    return async () => {
      const { data } = await this.get(path, { params });
      return data;
    };
  }

  /**
   * Prepare a call to list resources
   *
   * @param {string} objectURI API object relative URI or path
   * @param {object} params Query parameters
   * @returns {Promise} a promise
   */
  list(objectURI, params = {}) {
    const paramsWithDefaults = { ...{ limit: 60 }, ...params };
    this.requestParams = { method: 'GET', params: paramsWithDefaults, path: objectURI };
    return async () => {
      const response = await this.get(objectURI, { params: paramsWithDefaults });
      return handleListData(response);
    };
  }
}
